import type { TFunction } from "i18next";
import i18n from "i18next";

import { Handbook } from "features/handbook/model/handbook";
import {
  AnyRow,
  normalizeOptionalRef,
  normalizeRef,
} from "features/handbook/lib/refValue";
import {
  CustomSelect,
  dataSourceStatusOptions,
  divisionOptions,
} from "shared/ui/TableSelect";
import { RichTextView } from "shared/ui/richText/RichTextView";
import {
  HandbookColumn,
  HandbookColumnFlags,
  HandbookRefFields,
} from "./handbookColumns.types";
import styles from "./HandbookTable.module.css";

export const fmt = (v: unknown) =>
  v === null || v === undefined || v === "" ? "—" : String(v);

type BuildOptions = {
  t: TFunction;
  flags: HandbookColumnFlags;
  refFields: HandbookRefFields;
  isAgroCredit?: boolean;
};

const isRu = () => i18n.language === "ru";

/** Локализованное значение ссылки — язык выбирается так же, как в заголовке. */
const refName = (row: Handbook, fieldKey: string | null) => {
  const ref = normalizeRef(row as unknown as AnyRow, fieldKey);
  return fmt(isRu() ? ref?.nameRu : ref?.nameKy);
};

export function buildHandbookColumns({
  t,
  flags,
  refFields,
  isAgroCredit,
}: BuildOptions): HandbookColumn[] {
  const byLang = (ruKey: string, kyKey: string) => t(isRu() ? ruKey : kyKey);

  /** Колонка-ссылка на другой справочник: заголовок из пары ключей, значение — имя ссылки. */
  const refColumn = (
    key: string,
    ruKey: string,
    kyKey: string,
    fieldKey: string | null,
  ): HandbookColumn => ({
    key,
    header: byLang(ruKey, kyKey),
    render: (row) => refName(row, fieldKey),
  });

  const text = (value: unknown) => (
    <span className={styles.cellValue}>{fmt(value)}</span>
  );

  const columns: Array<HandbookColumn | false | undefined> = [
    flags.alias && {
      key: "alias",
      header: "Алиас",
      render: (row) => text(row.alias),
    },

    flags.unitMeasurement && {
      key: "unitMeasurement",
      header: t("handbook.table.unitMeasurement"),
      render: (row) => {
        const ref = normalizeOptionalRef(
          row as unknown as AnyRow,
          refFields.unitMeasurement,
        );
        return fmt(isRu() ? ref?.nameRu : ref?.nameKy);
      },
    },

    flags.division && {
      key: "division",
      header: t("handbook.table.division"),
      render: (row) => (
        <CustomSelect
          disebled={true}
          value={row.division ?? ""}
          options={divisionOptions}
        />
      ),
    },

    flags.dataSourceStatus && {
      key: "dataSourceStatus",
      header: t("handbook.table.dataSourceStatus"),
      render: (row) => (
        <CustomSelect
          disebled={true}
          value={row.dataSourceStatus ?? ""}
          options={dataSourceStatusOptions}
        />
      ),
    },

    flags.category &&
      refColumn(
        "category",
        "handbook.table.categoryNameRu",
        "handbook.table.categoryNameKy",
        refFields.category,
      ),

    flags.bik && {
      key: "bik",
      header: byLang("handbook.table.bikNameRu", "handbook.table.bikNameKy"),
      // Историческая особенность: в ячейке подпись всегда кыргызская.
      label: t("handbook.table.bikNameKy"),
      render: (row) => text(row.bik),
    },

    flags.region && {
      key: "region",
      header: "Регион",
      render: (row) => refName(row, refFields.region),
    },

    flags.bank && {
      key: "bank",
      header: "Банк",
      render: (row) => text(row.bank?.nameRu),
    },

    flags.descriptionRu && {
      key: "descriptionRu",
      header: t("handbook.table.descriptionRu"),
      render: (row) =>
        isAgroCredit ? (
          <RichTextView html={row.descriptionRu} />
        ) : (
          text(row.descriptionRu)
        ),
    },

    flags.descriptionKy && {
      key: "descriptionKy",
      header: t("handbook.table.descriptionKy"),
      render: (row) =>
        isAgroCredit ? (
          <RichTextView html={row.descriptionKy} />
        ) : (
          text(row.descriptionKy)
        ),
    },

    flags.type &&
      refColumn(
        "type",
        "handbook.table.typeNameRu",
        "handbook.table.typeNameKy",
        refFields.type,
      ),

    flags.order && {
      key: "order",
      header: byLang(
        "handbook.table.orderNameRu",
        "handbook.table.orderNameKy",
      ),
      // Историческая особенность: в ячейке подпись всегда русская.
      label: t("handbook.table.orderNameRu"),
      cellStyle: { maxWidth: "40px" },
      render: (row) => fmt(row.order),
    },

    flags.thresholdPerM2 && {
      key: "thresholdPerM2",
      header: "Порог вредоносности, экз./м²",
      render: (row) => fmt(row.thresholdPerM2),
    },

    flags.crop &&
      refColumn(
        "crop",
        "handbook.table.cropNameRu",
        "handbook.table.cropNameKy",
        refFields.crop,
      ),

    flags.address && {
      key: "address",
      header: "Адрес",
      render: (row) => fmt(row.address),
    },
  ];

  return columns.filter((column): column is HandbookColumn => !!column);
}
